/**
 * AB#38014 fix — single source of truth for "is Quick Entry the active mode,"
 * extracted out of DataEntryDetails.isQuickEntryEligible() so it can also be
 * read live from redux (via a select() effect) at the moment
 * uploadMultipleDocumentsSaga decides whether to auto-fire the intake
 * extraction, instead of trusting a value snapshotted at upload-click time
 * that can go stale while the upload is still in flight (facilityConfiguration
 * or transactionDetails resolving asynchronously afterward).
 *
 * DataEntryDetails.isQuickEntryEligible() delegates to this same function so
 * there remains exactly one implementation of the eligibility rule.
 */
export interface QuickEntryEligibilityInputs {
    intakeIntelligenceFlagEnabled: boolean;
    facilityIntakeIntelligenceEnabled: boolean;
    quickEntryFlagEnabled: boolean;
    classicModeOverride: boolean;
    /** LandingPageDucks.ts requestContext; undefined defaults to 1 (its own initial state). */
    requestContext: number | undefined;
    transactionStatusId: number | undefined;
}

/**
 * Bug fix (AB#38082): the requestContext half of the rule above, on its own. LandingPageDucks
 * needs to ask "would this queue context force Classic Mode?" before it clears a context the new
 * transaction did not come from, and asking it here keeps the eligible set defined once.
 *
 * 1 = the default/reset value, 2 = Data Entry; both mean "this transaction is in the Data Entry
 * stage". 3-8 are the later-stage queues, which only Classic Mode's 4-tab layout can serve.
 */
export function isQuickEntryRequestContext(requestContext: number | undefined): boolean {
    const context = requestContext ?? 1;
    return context === 1 || context === 2;
}

export function isQuickEntryEligibleFromInputs(inputs: QuickEntryEligibilityInputs): boolean {
    const requestContextEligible = isQuickEntryRequestContext(inputs.requestContext);
    const dataEntryStatusEligible = inputs.transactionStatusId != null && inputs.transactionStatusId < 50;

    return Boolean(inputs.intakeIntelligenceFlagEnabled)
        && Boolean(inputs.facilityIntakeIntelligenceEnabled)
        && Boolean(inputs.quickEntryFlagEnabled)
        && !inputs.classicModeOverride
        && requestContextEligible
        && dataEntryStatusEligible;
}
